// MoveControlDialog — move a control to a different control category.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useApi } from '@/lib/api/client'
import { useTenant } from '@/components/shared/TenantShell'
import { controlsApi } from '@/lib/api/controls'
import { categoriesApi, type Category } from '@/lib/api/categories'
import { queryKeys } from '@/lib/api/queryKeys'
import { HierarchyPickerDialog, type HierarchyNode } from '@/components/shared/HierarchyPickerDialog'

interface MoveControlDialogProps {
  open: boolean
  onClose: () => void
  onMoved: () => void
  controlId: string
  currentCategoryId: string
  currentCategoryName: string
}

export function MoveControlDialog({
  open,
  onClose,
  onMoved,
  controlId,
  currentCategoryId,
  currentCategoryName,
}: MoveControlDialogProps) {
  const api = useApi()
  const { slug } = useTenant()
  const queryClient = useQueryClient()
  const ctrlApi = controlsApi(api)
  const catApi = categoriesApi(api, 'control')

  const { data: categories } = useQuery({
    queryKey: queryKeys.categories.tree(slug, 'control'),
    queryFn: () => catApi.tree(),
    enabled: open,
  })

  const moveMutation = useMutation({
    mutationFn: ({ destId, notes }: { destId: string; notes: string }) =>
      ctrlApi.move(controlId, destId, notes),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.controls.detail(slug, controlId) })
      queryClient.invalidateQueries({ queryKey: queryKeys.categories.tree(slug, 'control') })
      queryClient.invalidateQueries({ queryKey: queryKeys.controls.all(slug) })
      onMoved()
    },
  })

  const nodes: HierarchyNode[] = (categories ?? []).map((c: Category) => ({
    id: c.id,
    name: c.name,
    parent_id: c.parent_id,
  }))

  function handleConfirm(selectedId: string, changeNotes: string) {
    moveMutation.mutate({ destId: selectedId, notes: changeNotes })
  }

  return (
    <HierarchyPickerDialog
      open={open}
      onClose={onClose}
      onConfirm={handleConfirm}
      title="Move Control"
      subtitle={`Current category: ${currentCategoryName}`}
      nodeLabel="category"
      nodes={nodes}
      currentId={currentCategoryId}
      isPending={moveMutation.isPending}
      error={moveMutation.error ? (moveMutation.error as Error).message : null}
    />
  )
}
